<template>
  <div class="settings-chatbot">
    <div class="settings-chatbot__title">{{ $tr('vacancy.chatbot.title') }}</div>

    <div v-if="connections.length === 0" class="settings-chatbot__disclaimer">
      {{ $tr('vacancy.chatbot.need-to-link') }}

      <router-link tag="a" :to="{ name: 'settings-chatbot' }">
        {{ $tr('vacancy.chatbot.company-settings') }}
      </router-link>
    </div>

    <vpreloader v-if="isLoading" width="2" size="64" />

    <div v-else class="settings-chatbot__body">
      <div v-if="connections.length > 0" class="settings-chatbot__list">
        <div class="settings-chatbot__list-item">
          <div class="settings-chatbot__list-item-name">
            <strong>{{ $tr('vacancy.chatbot.bot') }}</strong>
          </div>

          <div class="settings-chatbot__list-item-name">
            <strong>{{ $tr('vacancy.chatbot.description') }}</strong>
          </div>
        </div>

        <div v-for="(item, index) in connections" :key="item.id" class="settings-chatbot__list-item">
          <div class="settings-chatbot__list-item-name">
            <div v-if="item._embedded !== undefined">
              <a :href="`//t.me/${item._embedded.connector.username}`" target="_blank">
                @{{ item._embedded.connector.username }}
              </a>
            </div>

            <div v-if="item._embedded !== undefined" class="settings-chatbot__list-item-type">
              {{ getMessengerItem(item._embedded.connector.messenger_type).name }}
            </div>

            <div class="settings-chatbot__list-item-actions">
              <div class="settings-chatbot__list-item-action">
                <trigger
                  v-model="item.registration_enabled"
                  :disabled="isLoadingForms"
                  :text="$tr('vacancy.chatbot.enable-upon-registration')"
                  @input="updateVacancy(item.id, index)"
                />
              </div>

              <div class="settings-chatbot__list-item-action">
                <trigger
                  v-model="item.survey_enabled"
                  :disabled="isLoadingForms"
                  :text="$tr('vacancy.chatbot.enable-when-polling')"
                  @input="updateVacancy(item.id, index)"
                />
              </div>
            </div>
          </div>

          <div class="settings-chatbot__list-item-description">
            <vtextarea v-model="item.description" :height="120" @input="updateVacancy(item.id, index)" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import Axios from 'axios';
import Trigger from '@/components/shared/Trigger';
import debounce from 'debounce';

export default {
  components: {
    Trigger
  },

  data() {
    return {
      isLoading: false,
      isLoadingForms: false,
      messengerOptions: [{ code: 1, name: 'Telegram' }],
      connections: []
    };
  },

  computed: {
    ...mapState('vacancy_new', { vacancy: 'item' })
  },

  async created() {
    await this.fetchChatbotConnectors();
    await this.fetchChatbotVacancies();
  },

  methods: {
    async fetchChatbotConnectors() {
      this.isLoading = true;

      await Axios.get('/v3/chatbot/connectors?filter[has_vacancy]=false')
        .then(response => {
          if (response.data._embedded.connectors !== undefined) {
            this.connections = response.data._embedded.connectors.map(x => {
              return {
                id: null,
                vacancy_id: this.vacancy.id,
                survey_enabled: false,
                registration_enabled: false,
                connector_id: x.id,
                description: '',
                _embedded: {
                  connector: {
                    id: x.id,
                    messenger_type: x.messenger_type,
                    username: x.username
                  }
                }
              };
            });
          }
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    async fetchChatbotVacancies() {
      this.isLoading = true;

      await Axios.get(`/v3/chatbot/vacancies?with[]=connector&filter[vacancy_id]=${this.vacancy.id}`)
        .then(response => {
          const vacancies = response.data._embedded.chatbots;

          // Собираем Коннекторы и Вакансии в один массив к одному виду (отображаемый список)
          if (vacancies !== undefined && vacancies.length > 0) {
            this.connections = this.connections.concat(vacancies);
          }
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    getMessengerItem(messengerType) {
      const messenger = this.messengerOptions.find(x => x.code === Number(messengerType));

      if (messenger !== undefined) {
        return messenger;
      }
    },

    updateVacancy: debounce(async function (vacancyId, connectionIndex) {
      this.isLoadingForms = true;
      // Если в списке (this.connections) не было id (id === null), то значит нужно создать связь (POST)
      if (vacancyId !== null) {
        const currentVacancy = this.connections.find(x => x.id == vacancyId);
        const params = {
          survey_enabled: currentVacancy.survey_enabled,
          registration_enabled: currentVacancy.registration_enabled,
          connector_id: currentVacancy.connector_id,
          vacancy_id: Number(this.vacancy.id),
          description: currentVacancy.description
        };

        await Axios.patch(`/v3/chatbot/vacancies/${vacancyId}`, params)
          .then(response => {
            if (response.status === 200 || response.status === 201) {
              this.$noty.success(this.$tr('vacancy.basic.form.modules.informers.save_success'));
            }
          })
          .catch(() => {
            this.$noty.success(this.$tr('vacancy.basic.form.modules.informers.save_error'));
          })
          .finally(() => {
            this.isLoadingForms = false;
          });
      } else {
        const currentVacancy = this.connections[connectionIndex];
        const params = {
          survey_enabled: currentVacancy.survey_enabled,
          registration_enabled: currentVacancy.registration_enabled,
          connector_id: currentVacancy.connector_id,
          vacancy_id: Number(this.vacancy.id),
          description: currentVacancy.description
        };

        await Axios.post('/v3/chatbot/vacancies', params)
          .then(async response => {
            if (response.status === 200 || response.status === 201) {
              this.$noty.success(this.$tr('vacancy.basic.form.modules.informers.save_success'));

              await this.fetchChatbotConnectors();
              await this.fetchChatbotVacancies();
            }
          })
          .catch(() => {
            this.$noty.success(this.$tr('vacancy.basic.form.modules.informers.save_error'));
          })
          .finally(() => {
            this.isLoadingForms = false;
          });
      }
    }, 600)
  }
};
</script>

<style lang="scss" scoped>
@import '@/styles/fonts.scss';

.settings-chatbot {
  max-width: 800px;

  &__title {
    @include mixin-text-header-bigger;
  }

  &__disclaimer {
    @include mixin-text-main;
    margin-top: 15px;
  }

  &__field {
    margin-bottom: 20px;
  }

  &__field-label {
    margin-bottom: 5px;
    color: #666;
    font-size: 14px;
  }

  &__field-select {
    width: 100%;
    max-width: 100%;
  }

  &__field-button {
    margin-top: 30px;
  }

  &__list {
    margin-top: 45px;
    max-width: 800px;
  }

  &__list-item-name {
    @include mixin-text-main;
    flex: 0 1 400px;
  }

  &__list-item {
    padding: 15px 0;
    border-bottom: 1px solid #eee;
    display: flex;
    align-items: center;
    justify-content: space-between;

    > div {
      padding: 0 10px;
    }
  }

  &__list-item-type {
    color: #666;
    font-size: 14px;
    margin-top: 7px;
  }

  &__list-item-actions {
    margin-top: 20px;
  }

  &__list-item-action {
    margin-bottom: 12px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  &__list-item-description {
    flex: 1 1 auto;
  }
}
</style>
